import { UsercryptCheckinEntrySummary } from '@calm/evexia-web-sdk';
import { useActiveCircle } from '@rhg/hooks/use-active-circle';
import createUsercryptApi from '@rhg/libs/api/create-usercrypt-api';
import useAuth from '@rhg/libs/auth0/use-auth';
import {
    checkQueryCache,
    ItemToQueryMapping,
    queryClient,
    queryKeys,
    useQuery,
} from '@rhg/libs/react-query';

// Individual entries do not expire on their own — they are invalidated explicitly
// when a new checkin entry is added via useAddCheckinEntry.
const CHECKIN_ENTRY_STALE_TIME = Infinity;

export const latestCheckinEntriesQueryConfig = {
    queryKey: (recipientId: number | undefined, checkinTypeKeys: string[]) => [
        queryKeys.checkinEntriesLatest,
        recipientId,
        checkinTypeKeys.reduce<Record<string, string>>(
            (acc, key) => ({ ...acc, [key]: key }),
            {}
        ),
    ],
    queryFn: async ({
        checkinTypeKeys,
        circleAuthorizationToken,
        recipientId,
        getAccessTokenSilently,
    }: {
        checkinTypeKeys: string[];
        circleAuthorizationToken: string;
        recipientId: number;
        getAccessTokenSilently: () => Promise<string>;
    }): Promise<Record<string, UsercryptCheckinEntrySummary>> => {
        const keyObjects = checkinTypeKeys.map(
            (key): ItemToQueryMapping<string> => ({
                item: key,
                queryKey: latestCheckinEntriesQueryConfig.queryKey(
                    recipientId,
                    [key]
                ),
            })
        );

        const { cachedQueries, invalidQueries } = checkQueryCache<
            string,
            Record<string, UsercryptCheckinEntrySummary>
        >(keyObjects, CHECKIN_ENTRY_STALE_TIME);

        const cachedEntries = cachedQueries.reduce<
            Record<string, UsercryptCheckinEntrySummary>
        >((acc, { data, item }) => {
            const entry = data[item];
            return entry ? { ...acc, [item]: entry } : acc;
        }, {});

        if (invalidQueries.length === 0) {
            return cachedEntries;
        }

        const keysToFetch = invalidQueries.map(({ item }) => item);

        const usercryptApi = createUsercryptApi(getAccessTokenSilently);
        const response = await usercryptApi.usercryptGetCheckinLatestEntries({
            circleAuthorizationToken,
            recipientId,
            checkinTypeKey: keysToFetch,
        });

        const fetchedEntries = (response.data.summaries ?? []).reduce<
            Record<string, UsercryptCheckinEntrySummary>
        >(
            (acc, summary) =>
                summary.meta?.key
                    ? { ...acc, [summary.meta.key]: summary }
                    : acc,
            {}
        );

        // Cache each fetched entry individually so future calls for a subset
        // of keys can reuse them without re-fetching.
        cacheCheckinEntries(fetchedEntries, recipientId);

        return { ...cachedEntries, ...fetchedEntries };
    },
    queryOptions: ({
        isAuthenticated,
        circleAuthorizationToken,
        recipientId,
    }: {
        isAuthenticated: boolean;
        circleAuthorizationToken: string | undefined;
        recipientId: number | undefined;
    }) => ({
        enabled:
            isAuthenticated &&
            typeof circleAuthorizationToken === 'string' &&
            typeof recipientId === 'number',
    }),
};

export function cacheCheckinEntries(
    entries: Record<string, UsercryptCheckinEntrySummary>,
    recipientId: number,
    _queryClient = queryClient
) {
    Object.entries(entries).forEach(([key, entry]) => {
        _queryClient.setQueryData(
            latestCheckinEntriesQueryConfig.queryKey(recipientId, [key]),
            { [key]: entry }
        );
    });
}

export function useLatestCheckinEntries(checkinTypeKeys: string[]) {
    const { activeCircle: circle } = useActiveCircle();
    const { isAuthenticated, getAccessTokenSilently } = useAuth();

    const { data, ...otherProps } = useQuery({
        queryKey: latestCheckinEntriesQueryConfig.queryKey(
            circle?.recipient.id,
            checkinTypeKeys
        ),
        queryFn: async () =>
            latestCheckinEntriesQueryConfig.queryFn({
                checkinTypeKeys,
                circleAuthorizationToken: circle!.authorizationJWT!,
                recipientId: circle!.recipient.id,
                getAccessTokenSilently,
            }),
        ...latestCheckinEntriesQueryConfig.queryOptions({
            isAuthenticated,
            circleAuthorizationToken: circle?.authorizationJWT,
            recipientId: circle?.recipient.id,
        }),
    });

    return {
        latestCheckinEntries: data ?? {},
        ...otherProps,
    };
}
